import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css';

// Test accounts seeded by the API (DemoSeeder + the super admin seed).
const DEMO_ACCOUNTS = [
  {
    userName: 'demo',
    password: 'Demo@123',
    labelKey: 'auth.login.demoAccounts.demo',
    icon: 'fa-house-user',
  },
  {
    userName: 'agarlive',
    password: 'Abc123@',
    labelKey: 'auth.login.demoAccounts.superAdmin',
    icon: 'fa-user-shield',
  },
  {
    userName: 'Naded',
    password: 'Abc123@',
    labelKey: 'auth.login.demoAccounts.orgAdmin',
    icon: 'fa-user-tie',
  },
  {
    // Owners sign in with their national ID, so that is the user name.
    userName: '1010101010',
    password: 'Owner@123',
    labelKey: 'auth.login.demoAccounts.owner',
    icon: 'fa-house-chimney-user',
  },
];

const SHOW_DEMO_ACCOUNTS = true;

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    userName: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const signIn = async (userName, password) => {
    setError('');
    setLoading(true);

    const result = await login(userName, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.message || t('auth.login.loginFailed'));
    }

    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.userName.trim() || !formData.password.trim()) {
      setError(t('auth.login.usernamePasswordRequired'));
      return;
    }

    await signIn(formData.userName, formData.password);
  };

  // Fill the fields so it is obvious which account was used, then sign in.
  const signInAsDemo = async (account) => {
    setFormData({ userName: account.userName, password: account.password });
    await signIn(account.userName, account.password);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo-container">
            <img
              src="/logo.png"
              alt="Logo"
              className="login-logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="login-logo-fallback" style={{ display: 'none' }}>
              <i className="fas fa-building"></i>
            </div>
          </div>
          <h1 className="login-title">{t('auth.login.title')}</h1>
          <p className="login-subtitle">{t('auth.login.subtitle')}</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="login-alert" role="alert">
              <i className="fas fa-circle-exclamation"></i>
              {error}
            </div>
          )}

          <div className="login-field">
            <label htmlFor="userName" className="login-label">
              {t('auth.login.username')}
            </label>
            <span className="login-input-wrap">
              <i className="fas fa-user login-input-icon"></i>
              <input
                type="text"
                id="userName"
                name="userName"
                className="login-input"
                value={formData.userName}
                onChange={handleChange}
                placeholder={t('auth.login.enterUsername')}
                autoComplete="username"
                disabled={loading}
                required
              />
            </span>
          </div>

          <div className="login-field">
            <label htmlFor="password" className="login-label">
              {t('auth.login.password')}
            </label>
            <span className="login-input-wrap">
              <i className="fas fa-lock login-input-icon"></i>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className="login-input"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.login.enterPassword')}
                autoComplete="current-password"
                disabled={loading}
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                aria-label={t('auth.login.password')}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </span>
          </div>

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? t('auth.login.signingIn') : t('auth.login.signIn')}
          </button>
        </form>

        {SHOW_DEMO_ACCOUNTS && (
          <div className="demo-accounts">
            <div className="demo-accounts-title">
              <i className="fas fa-flask"></i>
              {t('auth.login.demoAccounts.title')}
            </div>
            <div className="demo-accounts-list">
              {DEMO_ACCOUNTS.map((account) => (
                <button
                  key={account.userName}
                  type="button"
                  className="demo-account-btn"
                  onClick={() => signInAsDemo(account)}
                  disabled={loading}
                >
                  <i className={`fas ${account.icon}`}></i>
                  <span className="demo-account-role">{t(account.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="login-help-text">
          <i className="fas fa-circle-info"></i> {t('auth.login.needHelp')}
        </p>
      </div>
    </div>
  );
};

export default Login;

